import { NUMERIC_PATTERNS } from '~/components/ui/forms/numericPatterns';

export type NumericInputConstraints = {
    allowNegative: boolean;
    allowDecimal: boolean;
    maxDecimals?: number;
};

export type NumericInputRules = {
    domPattern: string;
    isPartialAllowed: (value: string) => boolean;
    inputMode: 'decimal' | 'numeric';
};

const PRESET_CONSTRAINTS = new Map<string, NumericInputConstraints>([
    [NUMERIC_PATTERNS.INTEGER, { allowNegative: false, allowDecimal: false, maxDecimals: 0 }],
    [NUMERIC_PATTERNS.MONEY, { allowNegative: false, allowDecimal: true, maxDecimals: 2 }],
    [NUMERIC_PATTERNS.PERCENTAGE_4, { allowNegative: false, allowDecimal: true, maxDecimals: 4 }],
    [
        NUMERIC_PATTERNS.DECIMAL,
        { allowNegative: false, allowDecimal: true, maxDecimals: undefined },
    ],
    [
        NUMERIC_PATTERNS.SIGNED_DECIMAL,
        { allowNegative: true, allowDecimal: true, maxDecimals: undefined },
    ],
    [NUMERIC_PATTERNS.SIGNED_MONEY, { allowNegative: true, allowDecimal: true, maxDecimals: 2 }],
]);

export function stripPatternAnchors(pattern: string): string {
    return pattern.replace(/^\^/, '').replace(/\$$/, '');
}

export function extractPatternConstraints(pattern: string): NumericInputConstraints | null {
    const preset = PRESET_CONSTRAINTS.get(pattern);
    if (preset) return preset;

    const allowNegative = pattern.includes('-?');
    const limitedDecimal = pattern.match(/\(\\\.\\d\{0,(\d+)\}\)\?/);
    if (limitedDecimal) {
        return {
            allowNegative,
            allowDecimal: true,
            maxDecimals: Number(limitedDecimal[1]),
        };
    }

    if (/\(\\\.\\d\*\)\?/.test(pattern)) {
        return {
            allowNegative,
            allowDecimal: true,
            maxDecimals: undefined,
        };
    }

    if (/^\^(-?\\d+)\\+\$$/.test(pattern)) {
        return {
            allowNegative,
            allowDecimal: false,
            maxDecimals: 0,
        };
    }

    return null;
}

function derivePartialPattern(pattern: string, allowNegative: boolean): string {
    let body = stripPatternAnchors(pattern);
    body = body.replace(/\\d\+/g, '\\d*').replace(/\[0-9\]\+/g, '[0-9]*');
    if (allowNegative && !body.startsWith('-?')) {
        body = `-?${body}`;
    }
    return body;
}

export function resolveNumericInputRules(opts: {
    pattern?: string;
    partialPattern?: string;
    min?: number | string;
}): NumericInputRules {
    const pattern = opts.pattern ?? NUMERIC_PATTERNS.DECIMAL;
    const allowNegativeFromMin = opts.min === undefined || Number(opts.min) < 0;

    if (opts.partialPattern) {
        const partial = stripPatternAnchors(opts.partialPattern);
        const partialRe = new RegExp(`^${partial}$`);
        const extracted = extractPatternConstraints(pattern);
        const constraints = extracted
            ? {
                  ...extracted,
                  allowNegative: extracted.allowNegative || allowNegativeFromMin,
              }
            : null;

        return {
            domPattern: constraints
                ? numericInputPattern(constraints)
                : stripPatternAnchors(pattern),
            isPartialAllowed: (value) => {
                if (value === '') return true;
                if (value === '-' && allowNegativeFromMin) return true;
                return partialRe.test(value);
            },
            inputMode: constraints
                ? numericInputMode(constraints)
                : pattern.includes('.')
                  ? 'decimal'
                  : 'numeric',
        };
    }

    const extracted = extractPatternConstraints(pattern);
    if (extracted) {
        const constraints: NumericInputConstraints = {
            ...extracted,
            allowNegative: extracted.allowNegative || allowNegativeFromMin,
        };
        return {
            domPattern: numericInputPattern(constraints),
            isPartialAllowed: (value) => isPartialNumericInput(value, constraints),
            inputMode: numericInputMode(constraints),
        };
    }

    const partial = derivePartialPattern(pattern, allowNegativeFromMin);
    const partialRe = new RegExp(`^${partial}$`);

    return {
        domPattern: stripPatternAnchors(pattern),
        isPartialAllowed: (value) => {
            if (value === '') return true;
            if (value === '-' && allowNegativeFromMin) return true;
            return partialRe.test(value);
        },
        inputMode: pattern.includes('.') ? 'decimal' : 'numeric',
    };
}

export function numericInputConstraints(
    min?: number | string,
    maxDecimals?: number,
): NumericInputConstraints {
    return {
        allowNegative: min === undefined || Number(min) < 0,
        allowDecimal: maxDecimals !== 0,
        maxDecimals,
    };
}

export function isPartialNumericInput(
    value: string,
    { allowNegative, allowDecimal, maxDecimals }: NumericInputConstraints,
): boolean {
    if (value === '') return true;
    const sign = allowNegative ? '-?' : '';
    const decimalCount = maxDecimals !== undefined ? `{0,${maxDecimals}}` : '*';
    const decimals = allowDecimal ? `(\\.\\d${decimalCount})?` : '';
    return new RegExp(`^${sign}\\d*${decimals}$`).test(value);
}

export function numericInputPattern({
    allowNegative,
    allowDecimal,
    maxDecimals,
}: NumericInputConstraints): string {
    const decimalCount = maxDecimals !== undefined ? `{0,${maxDecimals}}` : '*';
    return `${allowNegative ? '-?' : ''}[0-9]*${allowDecimal ? `[.]?[0-9]${decimalCount}` : ''}`;
}

export function numericInputMode({ allowDecimal }: NumericInputConstraints): 'decimal' | 'numeric' {
    return allowDecimal ? 'decimal' : 'numeric';
}

export function parseNumericInput(value: string): number {
    return value === '' || value === '-' || value === '.' || value === '-.' ? NaN : Number(value);
}

export function applyNumericValidity(
    el: HTMLInputElement,
    min: number | string | undefined,
    max: number | string | undefined,
) {
    const n = parseNumericInput(el.value);
    let message = '';
    if (!isNaN(n)) {
        if (min !== undefined && n < Number(min)) {
            message = `Value must be greater than or equal to ${min}.`;
        } else if (max !== undefined && n > Number(max)) {
            message = `Value must be less than or equal to ${max}.`;
        }
    }
    el.setCustomValidity(message);
}

const MONEY_MAX_INTEGER_DIGITS = 8;
const MONEY_MAX_DECIMAL_DIGITS = 2;

function formatIntegerWithCommas(integer: string): string {
    return integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function sanitizeMoneyInput(value: string): string {
    let sanitized = value.replace(/[^\d.]/g, '').trim();
    if (sanitized.length > 1) {
        sanitized = sanitized.replace(/^0+(?!\.)/, '');
    }

    const dotIndex = sanitized.indexOf('.');
    let integer = dotIndex === -1 ? sanitized : sanitized.slice(0, dotIndex);
    let decimal = dotIndex === -1 ? '' : sanitized.slice(dotIndex + 1).replace(/\./g, '');

    integer = integer.slice(0, MONEY_MAX_INTEGER_DIGITS);
    decimal = decimal.slice(0, MONEY_MAX_DECIMAL_DIGITS);

    if (!integer && !decimal) {
        return '';
    }

    const formattedInteger = integer ? formatIntegerWithCommas(integer) : '0';
    const hasTrailingDot = dotIndex !== -1 && sanitized.endsWith('.') && decimal === '';

    if (hasTrailingDot) {
        return `${formattedInteger}.`;
    }

    return decimal.length > 0 ? `${formattedInteger}.${decimal}` : formattedInteger;
}

export function moneyToNumber(value: string): number {
    const sanitized = value.replace(/[^\d.]/g, '').trim();
    const parsed = parseFloat(sanitized);
    return isNaN(parsed) ? 0 : parsed;
}
